import {
  Button,
  SequenceMenu,
  Tooltip,
  RedRed60,
  Primary12Regular,
  Primary12SemiBold,
  Primary13Regular,
  GreyGrey60,
  GreyGrey20,
  GreyGrey10,
  Primary14SemiBold,
  IndigoIndigo10,
  IconEllipsisHorizontal,
  IconCoinStack,
  IconLayers,
  Badge,
  Center,
  Box,
  Flex,
  Grid,
  type IconComponent
} from '@sequencehq/core-components'
import { FC, Fragment, ReactNode } from 'react'
import { minimumRowActions, productRowActions } from './pricingTableRowActions'
import {
  MinimumRow as MinimumRowModel,
  PricingRow,
  usePricingTable
} from './usePricingTable'
import { BillingFrequency } from 'Cube/domain/cube.domain.types'
import { match } from 'ts-pattern'
import { endOfMonth, format, isValid, parse } from '@sequencehq/utils/dates'
import {
  formatOrdinal,
  getPricingModelLabel,
  toBillingType
} from '@sequencehq/utils'
import { renderPriceSecondaryRow } from './compact/PricingTableSecondaryRow'
import { DeleteCreditGrantModal } from 'Cube/view/common/widgets/Pricing/Phase/creditGrants/deleteCreditGrant.modal'
import { CreditGrantRowModel, useCreditGrantRows } from './useCreditGrantRows'

const styles = {
  tableHeader: {
    ...Primary12SemiBold,
    color: GreyGrey60,
    textTransform: 'uppercase' as const,
    border: 'none',
    padding: '10px 8px'
  },
  tableCell: {
    alignItems: 'center',
    ...Primary13Regular,
    border: 'none',
    textAlign: 'left' as const,
    padding: '8px',
    zIndex: 1
  }
}

export const getRepeatsDescription = (args: {
  recurrenceDayOfMonth?: number
  monthRecurrenceStarted?: number
  billingFrequency?: BillingFrequency
}) => {
  return match(args.billingFrequency)
    .with(undefined, () => '')
    .with('MONTHLY', () => {
      if (!args.recurrenceDayOfMonth) {
        return 'Monthly'
      }

      return `Monthly on the ${formatOrdinal(args.recurrenceDayOfMonth)}`
    })
    .with('QUARTERLY', () => {
      if (!args.recurrenceDayOfMonth) {
        return 'Quarterly'
      }

      return `Quarterly on the ${formatOrdinal(args.recurrenceDayOfMonth)}`
    })
    .with('YEARLY', () => {
      if (typeof args.monthRecurrenceStarted === 'undefined') {
        return 'Annually'
      }
      /**
       * 2025 is not a leap year, so it's a safe one to use. We need to set this hard coded year to account
       * for the potential for leap years to throw off the recurrence day if it's in February.
       */
      const recurrenceMonthAndDay = isValid(
        parse(
          `2025-${args.monthRecurrenceStarted + 1}-${args.recurrenceDayOfMonth}`,
          'yyyy-MM-dd',
          new Date()
        )
      )
        ? parse(
            `2025-${args.monthRecurrenceStarted + 1}-${args.recurrenceDayOfMonth}`,
            'yyyy-MM-dd',
            new Date()
          )
        : endOfMonth(new Date(`2025-${args.monthRecurrenceStarted + 2}-01`))
      /**
       * To do this well we need to grab the month that the product was introduced,
       * that is also still joined up with the product.
       */

      return `Annually on the ${format(recurrenceMonthAndDay, 'do MMM')}`
    })
    .with('ON_DEMAND', () => {
      return 'On demand'
    })
    .with('PER_EVENT', () => {
      return 'Per event'
    })
    .with('HALF_YEARLY', () => {
      if (!args.recurrenceDayOfMonth) {
        return 'Semi-annually'
      }

      return `Semi-annually on the ${formatOrdinal(args.recurrenceDayOfMonth)}`
    })
    .with('ONE_TIME', () => {
      return 'Once'
    })
    .exhaustive()
}

// "Quarterly / In advance" — merges the old Repeats + Bill columns into one.
const joinBilling = (parts: Array<string>): string =>
  parts.filter(Boolean).join(' / ')

const MINIMUM_TAG_LABEL = 'Minimum'

export const PricingTableExpanded: FC<{
  phaseId: string
}> = props => {
  const { validationErrors, pricingGroups, showArrCalculation } =
    usePricingTable({
      phaseId: props.phaseId
    })
  const {
    rows: creditGrantRows,
    grantToDelete,
    clearGrantToDelete,
    removeGrant
  } = useCreditGrantRows({ phaseId: props.phaseId })
  const columnWidths = [
    '1fr', // Name
    '240px', // Frequency
    ...(showArrCalculation ? ['90px'] : []), // ARR
    '140px', // Discount
    '1fr', // Price
    '36px' // Row menu
  ].join(' ')
  const columnConfig = { columnWidths, showArrCalculation }

  return (
    <Box data-testid="phase.pricingTable">
      <Box {...Primary14SemiBold} padding="16px 8px 8px 8px">
        Pricing
      </Box>
      <Box
        marginBottom="8px"
        border={validationErrors.length ? `1px solid ${RedRed60}` : ''}
        borderRadius="8px"
      >
        <Grid templateColumns={columnWidths} columnGap="8px">
          <Flex {...styles.tableHeader}>Name</Flex>
          <Flex {...styles.tableHeader}>Frequency</Flex>
          {showArrCalculation && <Flex {...styles.tableHeader}>ARR</Flex>}
          <Flex
            {...styles.tableHeader}
            data-testid="phase.pricingTable.header.discount"
          >
            Discount
          </Flex>
          <Flex {...styles.tableHeader} justifyContent="flex-end">
            Price
          </Flex>
          <Flex {...styles.tableHeader}></Flex>
        </Grid>
        {pricingGroups.map(group => (
          <Fragment
            key={`group${group.minimumRow?.id || group.pricingRows[0]?.id}}`}
          >
            {group.pricingRows.map(priceRow => (
              <PriceRow
                key={priceRow.id}
                priceRow={priceRow}
                hasMinimum={Boolean(group.minimumRow)}
                {...columnConfig}
              />
            ))}
          </Fragment>
        ))}
        {/* Minimums and grants render after all prices, as modifiers. */}
        {pricingGroups.map(group =>
          group.minimumRow ? (
            <MinimumRow
              key={`minimum-${group.minimumRow.id}`}
              minimumRow={group.minimumRow}
              appliedTo={group.pricingRows.map(priceRow => priceRow.name)}
              {...columnConfig}
            />
          ) : null
        )}
        {creditGrantRows.map(grant => (
          <CreditGrantRow
            key={`credit-${grant.id}`}
            grant={grant}
            {...columnConfig}
          />
        ))}
      </Box>
      {grantToDelete && (
        <DeleteCreditGrantModal
          creditGrantName={grantToDelete.name}
          onConfirm={() => removeGrant(grantToDelete.id)}
          onClose={clearGrantToDelete}
        />
      )}
    </Box>
  )
}

// Layout shared by every row so the modifier rows line up with the price rows.
type ColumnConfig = {
  columnWidths: string
  showArrCalculation: boolean
}

const PriceRow: FC<
  ColumnConfig & {
    priceRow: PricingRow
    hasMinimum: boolean
  }
> = ({ priceRow, hasMinimum, columnWidths, showArrCalculation }) => {
  const secondaryRow =
    priceRow.priceStructure && priceRow.priceCurrency
      ? renderPriceSecondaryRow({
          structure: priceRow.priceStructure,
          currency: priceRow.priceCurrency
        })
      : null
  const pricingModelLabel = priceRow.priceStructure
    ? getPricingModelLabel(priceRow.priceStructure)
    : null
  const hasSecondaryRow = Boolean(secondaryRow)

  return (
    <Grid
      data-test-is-highlighted={priceRow.highlighted}
      data-test-has-minimum={hasMinimum}
      backgroundColor="transparent"
      templateColumns={columnWidths}
      columnGap="8px"
      cursor="pointer"
      borderRadius="8px"
      minHeight="36px"
      _notLast={{ borderBottom: `1px solid ${GreyGrey20}` }}
      onClick={priceRow.onClick}
      _hover={{
        backgroundColor: GreyGrey10
      }}
      data-testid={`cube.priceRow.${priceRow.id}`}
      position="relative"
      _after={{
        content: '""',
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        borderRadius: '8px',
        backgroundColor: IndigoIndigo10,
        opacity: priceRow.highlighted ? 1 : 0,
        transition: 'opacity 0.5s ease-in-out',
        zIndex: 0
      }}
    >
      <Flex
        {...styles.tableCell}
        gap="6px"
        style={{
          textWrap: 'wrap',
          wordBreak: 'break-word',
          overflowWrap: 'anywhere'
        }}
        // Prices always sit at the base indent; minimums/grants are shown
        // separately as modifiers at the end of the table.
        paddingLeft="8px"
        gridRow={hasSecondaryRow ? '1 / 3' : undefined}
        alignItems={hasSecondaryRow ? 'flex-start' : 'center'}
        paddingTop={hasSecondaryRow ? '8px' : undefined}
      >
        {priceRow.name}
      </Flex>
      <Flex {...styles.tableCell} color={GreyGrey60}>
        {joinBilling([
          priceRow.billingFrequency
            ? getRepeatsDescription({
                recurrenceDayOfMonth: priceRow.recurrenceDayOfMonth,
                monthRecurrenceStarted: priceRow.monthRecurrenceStarted,
                billingFrequency: priceRow.billingFrequency
              })
            : '',
          priceRow.billingType ? toBillingType(priceRow.billingType) : ''
        ])}
      </Flex>
      {showArrCalculation && (
        <Flex
          {...styles.tableCell}
          color={GreyGrey60}
          data-testid={`cube.priceRow.${priceRow.id}.arrCalculation`}
        >
          {priceRow.phasePriceMetadata?.arrCalculation === 'EXCLUDE'
            ? 'Exclude'
            : 'Include'}
        </Flex>
      )}
      <Flex {...styles.tableCell}>
        {priceRow.discount ? (
          <Flex
            key={priceRow.discount.id}
            data-testid={`cube.priceRow.${priceRow.id}.discount`}
            alignContent="center"
            gap="4px"
            _hover={{
              opacity: 0.5
            }}
            onClick={e => {
              e.stopPropagation()
              priceRow.availableFeatures.canEditDiscount?.handler()
            }}
          >
            {priceRow.discount.label}
          </Flex>
        ) : null}
      </Flex>
      <Flex {...styles.tableCell} justifyContent="flex-end" gap="6px">
        <Tooltip label={pricingModelLabel ?? ''} placement="top">
          <Box>
            <Flex justifyContent="flex-end" gap="6px" alignItems="center">
              {priceRow.price !== priceRow.preDiscountPrice && (
                <Box
                  textDecoration="line-through"
                  textAlign="right"
                  color={GreyGrey60}
                  data-testid={`cube.priceRow.${priceRow.id}.preDiscountPrice`}
                >
                  {priceRow.preDiscountPrice}
                </Box>
              )}
              <Box
                textAlign="right"
                data-testid={`cube.priceRow.${priceRow.id}.price`}
              >
                {priceRow.price}
              </Box>
            </Flex>
          </Box>
        </Tooltip>
      </Flex>
      <RowMenu
        data-testid={`cube.priceRow.${priceRow.id}.menu`}
        items={productRowActions(priceRow)}
      />
      {secondaryRow && (
        <Box
          {...Primary12Regular}
          gridColumn="2 / -2"
          paddingX="8px"
          paddingBottom="8px"
          position="relative"
          zIndex={1}
          data-testid={`cube.priceRow.${priceRow.id}.secondaryRow`}
        >
          {secondaryRow}
        </Box>
      )}
    </Grid>
  )
}

const MinimumRow: FC<
  ColumnConfig & {
    minimumRow: MinimumRowModel
    appliedTo: string[]
  }
> = ({ minimumRow, appliedTo, columnWidths, showArrCalculation }) => (
  <Grid
    backgroundColor="transparent"
    data-testid={`cube.minimumRow.${minimumRow.id}`}
    data-test-is-highlighted={minimumRow.highlighted}
    templateColumns={columnWidths}
    columnGap="8px"
    alignItems="center"
    minHeight="36px"
    _hover={{ transition: 'none', backgroundColor: GreyGrey10 }}
    onClick={minimumRow.actions.edit.handler}
    cursor="pointer"
    position="relative"
    borderRadius="8px"
    _notLast={{ borderBottom: `1px solid ${GreyGrey20}` }}
    _after={{
      content: '""',
      position: 'absolute',
      borderRadius: '8px',
      top: '0',
      left: '0',
      right: '0',
      bottom: '0',
      backgroundColor: IndigoIndigo10,
      opacity: minimumRow.highlighted ? 1 : 0,
      transition: 'opacity 0.5s ease-in-out',
      zIndex: 0
    }}
  >
    <ModifierNameCell
      label={minimumRow.name}
      tag={MINIMUM_TAG_LABEL}
      tagIcon={IconLayers}
    />
    <Flex {...styles.tableCell} color={GreyGrey60}>
      {getRepeatsDescription({
        recurrenceDayOfMonth: minimumRow.recurrenceDayOfMonth,
        billingFrequency: minimumRow.billingFrequency
      })}
    </Flex>
    {showArrCalculation && <Flex {...styles.tableCell}></Flex>}
    <Flex {...styles.tableCell}></Flex>
    <Flex {...styles.tableCell} justifyContent="flex-end">
      {minimumRow.value}
    </Flex>
    <RowMenu items={minimumRowActions(minimumRow)} height="36px" />
    <AppliedToRow
      names={appliedTo}
      data-testid={`cube.minimumRow.${minimumRow.id}.appliedTo`}
    />
  </Grid>
)

const CreditGrantRow: FC<
  ColumnConfig & {
    grant: CreditGrantRowModel
  }
> = ({ grant, columnWidths, showArrCalculation }) => (
  <Grid
    data-testid={`cube.creditGrantRow.${grant.id}`}
    templateColumns={columnWidths}
    columnGap="8px"
    alignItems="center"
    minHeight="36px"
    onClick={grant.onView}
    cursor="pointer"
    borderRadius="8px"
    _notLast={{ borderBottom: `1px solid ${GreyGrey20}` }}
    _hover={{ backgroundColor: GreyGrey10 }}
  >
    <ModifierNameCell
      label={grant.name}
      tag={grant.typeLabel}
      tagIcon={IconCoinStack}
    />
    <Flex {...styles.tableCell} color={GreyGrey60}>
      {joinBilling([grant.repeats, grant.bill])}
    </Flex>
    {showArrCalculation && <Flex {...styles.tableCell}></Flex>}
    <Flex {...styles.tableCell}></Flex>
    <Flex {...styles.tableCell} justifyContent="flex-end">
      {grant.price}
    </Flex>
    <RowMenu
      data-testid={`cube.creditGrantRow.${grant.id}.menu`}
      height="36px"
      items={[
        {
          label: 'Delete credit',
          onClick: grant.onDelete,
          style: { color: RedRed60 },
          'data-testid': `cube.creditGrantRow.${grant.id}.delete`
        }
      ]}
    />
    {grant.grantedCredit && (
      <SecondaryRow data-testid={`cube.creditGrantRow.${grant.id}.granted`}>
        {grant.grantedCredit}
      </SecondaryRow>
    )}
    <AppliedToRow
      names={grant.appliedTo}
      data-testid={`cube.creditGrantRow.${grant.id}.appliedTo`}
    />
  </Grid>
)

// A modifier's name plus the badge marking what kind of modifier it is.
const ModifierNameCell: FC<{
  label: string
  tag: string
  tagIcon: IconComponent
}> = ({ label, tag, tagIcon }) => (
  <Flex
    {...styles.tableCell}
    {...Primary13Regular}
    gap="8px"
    alignItems="center"
    paddingY="6px"
    style={{ textWrap: 'wrap' }}
  >
    {label}
    <Badge sentiment="neutral" size="sm" textTransform="none" icon={tagIcon}>
      {tag}
    </Badge>
  </Flex>
)

// A line right-aligned under a row — the same slot as tier/seat secondary rows.
const SecondaryRow: FC<{
  children: ReactNode
  'data-testid'?: string
}> = ({ children, 'data-testid': dataTestId }) => (
  <Box
    {...Primary12Regular}
    color={GreyGrey60}
    gridColumn="2 / -2"
    paddingX="8px"
    paddingBottom="8px"
    textAlign="right"
    data-testid={dataTestId}
  >
    {children}
  </Box>
)

// The products a modifier (grant/minimum) applies to.
const AppliedToRow: FC<{
  names: string[]
  'data-testid'?: string
}> = ({ names, 'data-testid': dataTestId }) => (
  <SecondaryRow data-testid={dataTestId}>
    {names.map(name => (
      <Box key={name}>{name}</Box>
    ))}
  </SecondaryRow>
)

const RowMenu: FC<{
  items: Parameters<typeof SequenceMenu>[0]['items']
  height?: string
  'data-testid'?: string
}> = ({ items, height = '100%', 'data-testid': dataTestId }) => (
  <Flex
    {...styles.tableCell}
    maxWidth="36px"
    height={height}
    padding="0px"
    onClick={e => {
      e.stopPropagation()
    }}
  >
    <SequenceMenu
      alignment="bottom-right"
      data-testid={dataTestId}
      menuButton={
        <Button variant="ghost" size="24px">
          <Center flexShrink="0">
            <IconEllipsisHorizontal color={GreyGrey60} />
          </Center>
        </Button>
      }
      items={items}
    />
  </Flex>
)
